import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminAppBody from "../../../../components/admin/AdminAppBody";
import PageHeading from "../../../../components/admin/common/PageHeading";
import useApiCallHooks from "../../../../hooks/useApiCallHooks";
import { uiRoutes } from "../../../../routes/ui/uiRoutes";

export default function Attendance() {
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();
  const navigate = useNavigate();

  const today = getTodayYYYYMMDD();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    date: today,
    intime: "",
    tasksummary: "",
  });

  const errors = useMemo(() => validate(form), [form]);
  const isValid = Object.keys(errors).length === 0;

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  const onClickSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isValid) return;

    const payload = {
      date: form.date,
      in_time: form.intime,
      task_summary: form.tasksummary,
    };

    callAPI("post", "student/add-attendance", payload);
  };

  useEffect(() => {
    if (response?.data && statusCode === 201) {
      toast.success("Attendance saved successfully!");
      setTimeout(() => {
        navigate(uiRoutes.student.attendance.list);
      }, 1000);
    }
  }, [response, statusCode, navigate]);

  const totalMinutes = useMemo(() => {
    const inDt = getDateObj(form.date, form.intime);
    const outDt = getDateObj(form.date);
    if (!inDt || !outDt || outDt <= inDt) return null;
    return Math.round((outDt - inDt) / 60000);
  }, [form.date, form.intime,]);

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container">
          <PageHeading
            title="Add Attendance"
            subTitle="Fill out the details to record your attendance."
          />
          <div className="card p-4 shadow-sm mt-3">
            <form onSubmit={onClickSubmit} noValidate>
              {/* Date */}
              <div className="mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  name="date"
                  min={today}
                  max={today}
                  className={`form-control${
                    submitted && errors.date ? " is-invalid" : ""
                  }`}
                  value={form.date}
                  onChange={handleChange}
                />
                {submitted && errors.date && (
                  <div className="invalid-feedback">{errors.date}</div>
                )}
              </div>

              {/* In Time */}
              <div className="mb-3">
                <label className="form-label">In Time</label>
                <input
                  type="time"
                  name="intime"
                  className={`form-control${
                    submitted && errors.intime ? " is-invalid" : ""
                  }`}
                  value={form.intime}
                  onChange={handleChange}
                />
                {submitted && errors.intime && (
                  <div className="invalid-feedback">{errors.intime}</div>
                )}
              </div>

          

              {/* Task Summary */}
              <div className="mb-3">
                <label className="form-label">Task Summary</label>
                <textarea
                  name="tasksummary"
                  rows={3}
                  className={`form-control${
                    submitted && errors.tasksummary ? " is-invalid" : ""
                  }`}
                  value={form.tasksummary}
                  onChange={handleChange}
                />
                {submitted && errors.tasksummary && (
                  <div className="invalid-feedback">{errors.tasksummary}</div>
                )}
              </div>

              {/* Duration */}
              <div className="form-text mb-3">
                {totalMinutes != null
                  ? `Total: ${Math.floor(totalMinutes / 60)}h ${
                      totalMinutes % 60
                    }m`
                  : "Select valid In & Out time to see total duration."}
              </div>

              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? "Saving..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      }
    />
  );
}

// --- helpers ---
function getTodayYYYYMMDD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDateObj(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm || 0, 0, 0);
}

function validate({ date, intime, tasksummary }) {
  const errs = {};
  if (!date) errs.date = "Date is required";
  if (!intime) errs.intime = "In time is required";
  if (!tasksummary.trim()) errs.tasksummary = "Task Summary is required";
  return errs;
}
